import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

const ABOUT_SUBMENU_TOGGLE_ID = 'about-submenu-toggle';

function Header() {
  const location = useLocation();
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const timeoutRef = useRef(null);
  const aboutMenuRef = useRef(null);
  const aboutMenuItemRef = useRef(null);

  const isHomePage = location.pathname === '/';
  const isAboutActive = isHomePage;

  const scrollToSection = (sectionId, e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const el = document.getElementById(sectionId);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 92;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setIsAboutOpen(false);
    setIsMobileMenuOpen(false);
  };

  const goHome = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') window.location.href = '/';
    else window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsAboutOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleAbout = () => setIsAboutOpen((prev) => !prev);

  const openAbout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsAboutOpen(true);
  };

  const closeAbout = () => {
    timeoutRef.current = setTimeout(() => setIsAboutOpen(false), 150);
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleAboutKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsAboutOpen(false);
      document.getElementById(ABOUT_SUBMENU_TOGGLE_ID)?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isAboutOpen) setIsAboutOpen(true);
      requestAnimationFrame(() => {
        const firstLink = aboutMenuRef.current?.querySelector('a');
        if (firstLink) firstLink.focus();
      });
    }
  };

  const handleSubmenuLinkKeyDown = (e, index) => {
    if (e.key === 'ArrowUp' && index === 0) {
      e.preventDefault();
      document.getElementById(ABOUT_SUBMENU_TOGGLE_ID)?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsAboutOpen(false);
      document.getElementById(ABOUT_SUBMENU_TOGGLE_ID)?.focus();
    }
  };

  const handleAboutBlur = (e) => {
    if (!aboutMenuItemRef.current) return;
    if (e.relatedTarget && aboutMenuItemRef.current.contains(e.relatedTarget)) return;
    setIsAboutOpen(false);
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        if (isAboutOpen) {
          setIsAboutOpen(false);
          document.getElementById(ABOUT_SUBMENU_TOGGLE_ID)?.focus();
        }
      }
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [isAboutOpen]);

  useEffect(() => {
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  const mobileMenuContent = (
    <div
      className={`header-mobile-menu ${isMobileMenuOpen ? 'header-mobile-menu--open' : ''}`}
      id="mobile-menu"
      aria-hidden={!isMobileMenuOpen}
    >
      <div
        className="header-mobile-menu__backdrop"
        onClick={closeMobileMenu}
        aria-hidden="true"
      />
      <div className="header-mobile-menu__panel">
        <div className="header-mobile-menu__top">
          <span className="header-mobile-menu__title">Menu</span>
          <button
            type="button"
            className="header-mobile-menu__close"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>
        <nav className="header-mobile-menu__nav" aria-label="Mobile navigation">
          <ul className="header-mobile-menu__list">
            <li className="header-mobile-menu__item">
              <a href="/" className={`header-mobile-menu__link ${isAboutActive ? 'header-mobile-menu__link--active' : ''}`} onClick={goHome}>
                About Us
              </a>
              <ul className="header-mobile-menu__sublist">
                <li><a href="#our-goals" onClick={(e) => scrollToSection('our-goals', e)}>Our Goals</a></li>
                <li><a href="#guiding-principles" onClick={(e) => scrollToSection('guiding-principles', e)}>Guiding Principles</a></li>
                <li><a href="#our-culture" onClick={(e) => scrollToSection('our-culture', e)}>Our Culture</a></li>
                <li><a href="#our-name" onClick={(e) => scrollToSection('our-name', e)}>Our Name</a></li>
              </ul>
            </li>
            <li className="header-mobile-menu__item">
              <Link to="/our-team" className={`header-mobile-menu__link ${location.pathname === '/our-team' ? 'header-mobile-menu__link--active' : ''}`} onClick={closeMobileMenu}>
                Team
              </Link>
            </li>
            <li className="header-mobile-menu__item">
              <Link to="/contact-us" className={`header-mobile-menu__link ${location.pathname === '/contact-us' ? 'header-mobile-menu__link--active' : ''}`} onClick={closeMobileMenu}>
                Contact Us
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );

  return (
    <>
      <header className="site-header" role="banner">
        <div className="header-container">
          <div className="logo-container">
            <Link to="/" className="logo-link" aria-label="YK2 Partners home">
              <img src="/YK2-Logo.png" alt="YK2 Partners logo" className="logo-image" />
            </Link>
          </div>

          <nav className="main-navigation" role="navigation" aria-label="YK2 Partners Main Menu">
            <ul className="nav-menu">
              <li
                ref={aboutMenuItemRef}
                className={`menu-item menu-item-has-children ${isAboutActive ? 'active' : ''} ${isAboutOpen ? 'open' : ''}`}
                onMouseEnter={openAbout}
                onMouseLeave={closeAbout}
                onBlur={handleAboutBlur}
              >
                <div className="menu-item-inner">
                  <a
                    href="/"
                    className={isAboutActive ? 'active' : ''}
                    onClick={goHome}
                  >
                    About Us
                  </a>
                  <button
                    type="button"
                    id={ABOUT_SUBMENU_TOGGLE_ID}
                    className="submenu-toggle"
                    aria-expanded={isAboutOpen}
                    aria-controls="about-submenu"
                    aria-label={isAboutOpen ? 'Collapse About Us submenu' : 'Expand About Us submenu'}
                    onClick={toggleAbout}
                    onKeyDown={handleAboutKeyDown}
                  >
                    <span className="dropdown-arrow" aria-hidden="true" />
                  </button>
                </div>
                <div
                  ref={aboutMenuRef}
                  id="about-submenu"
                  role="region"
                  aria-labelledby={ABOUT_SUBMENU_TOGGLE_ID}
                  aria-hidden={!isAboutOpen}
                  className={`sub-menu ${isAboutOpen ? 'show' : ''}`}
                >
                  <a href="#our-goals" tabIndex={isAboutOpen ? undefined : -1} onClick={(e) => scrollToSection('our-goals', e)} onKeyDown={(e) => handleSubmenuLinkKeyDown(e, 0)}>Our Goals</a>
                  <a href="#guiding-principles" tabIndex={isAboutOpen ? undefined : -1} onClick={(e) => scrollToSection('guiding-principles', e)} onKeyDown={(e) => handleSubmenuLinkKeyDown(e, 1)}>Guiding Principles</a>
                  <a href="#our-culture" tabIndex={isAboutOpen ? undefined : -1} onClick={(e) => scrollToSection('our-culture', e)} onKeyDown={(e) => handleSubmenuLinkKeyDown(e, 2)}>Our Culture</a>
                  <a href="#our-name" tabIndex={isAboutOpen ? undefined : -1} onClick={(e) => scrollToSection('our-name', e)} onKeyDown={(e) => handleSubmenuLinkKeyDown(e, 3)}>Our Name</a>
                </div>
              </li>
              <li className="menu-item">
                <Link to="/our-team" className={location.pathname === '/our-team' ? 'active' : ''}>Team</Link>
              </li>
              <li className="menu-item">
                <Link to="/contact-us" className={location.pathname === '/contact-us' ? 'active' : ''}>Contact Us</Link>
              </li>
            </ul>
          </nav>

          <button
            type="button"
            className="header-mobile-toggle"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-controls="mobile-menu"
          >
            <span className="header-mobile-toggle__line" />
            <span className="header-mobile-toggle__line" />
            <span className="header-mobile-toggle__line" />
          </button>
        </div>
      </header>

      {createPortal(mobileMenuContent, document.body)}
    </>
  );
}

export default Header;
